import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { formatRupees, formatRupeesCompact } from "@/lib/format";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, LineChart, Line,
} from "recharts";
import { TrendingUp, CalendarRange, CalendarClock } from "lucide-react";

const PERIODS = [
  { key: "yoy", label: "Year on Year", icon: TrendingUp },
  { key: "quarterly", label: "Quarterly", icon: CalendarClock },
  { key: "monthly", label: "Monthly", icon: CalendarRange },
];

/**
 * <PerformanceView investmentId={optional} />
 *
 * When investmentId is provided, shows single-investment charts.
 * Otherwise, shows aggregated + per-investment breakdown.
 */
export default function PerformanceView({ investmentId, defaultPeriod = "yoy", compact = false }) {
  const [period, setPeriod] = useState(defaultPeriod);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    const q = new URLSearchParams({ period });
    if (investmentId) q.set("investment_id", investmentId);
    api.get(`/performance?${q.toString()}`)
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, [period, investmentId]);

  const totals = useMemo(() => {
    if (!data) return null;
    // Aggregate across investments per bucket
    const map = {};
    for (const b of data.buckets) map[b] = { bucket: b, income: 0, outflow: 0, net: 0 };
    for (const inv of data.investments) {
      for (const s of inv.series) {
        map[s.bucket].income += s.income;
        map[s.bucket].outflow += s.outflow;
        map[s.bucket].net += s.net;
      }
    }
    return Object.values(map);
  }, [data]);

  return (
    <div className="space-y-4">
      {/* Period switcher */}
      <div className="flex items-center gap-1 flex-wrap">
        {PERIODS.map((p) => (
          <button
            key={p.key}
            onClick={() => setPeriod(p.key)}
            data-testid={`perf-${p.key}`}
            className={`text-[11px] font-medium px-3 py-1.5 rounded-md border transition-colors inline-flex items-center gap-1.5 ${
              period === p.key
                ? "bg-slate-900 text-white border-slate-900"
                : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
            }`}
          >
            <p.icon className="h-3.5 w-3.5" /> {p.label}
          </button>
        ))}
      </div>

      {loading && (
        <div className="border border-slate-200 bg-white rounded-md p-10 text-center text-slate-400 text-[13px]">
          Loading…
        </div>
      )}

      {!loading && data && data.investments.length === 0 && (
        <div className="border border-slate-200 bg-white rounded-md p-10 text-center text-slate-500 text-[13px]">
          No investment data yet.
        </div>
      )}

      {!loading && data && data.investments.length > 0 && (
        <>
          {/* Aggregate chart (skip in compact single-investment mode) */}
          {!investmentId && totals && (
            <div className="border border-slate-200 bg-white rounded-md p-4">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <div className="eyebrow">Portfolio · Aggregate</div>
                  <div className="text-[14px] font-semibold text-slate-900 mt-0.5">
                    All investments — Income & Net cash flow
                  </div>
                </div>
              </div>
              <div className="h-[240px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={totals}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="bucket" tick={{ fontSize: 10 }} />
                    <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => formatRupeesCompact(v)} width={70} />
                    <Tooltip
                      formatter={(v, n) => [formatRupees(v), n]}
                      contentStyle={{ fontSize: 11, border: "1px solid #e2e8f0" }}
                    />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="income" fill="#16a34a" name="Income" radius={[3, 3, 0, 0]} />
                    <Bar dataKey="outflow" fill="#dc2626" name="Outflow" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Per-investment cards */}
          <div className={compact ? "" : "grid grid-cols-1 lg:grid-cols-2 gap-4"}>
            {data.investments.map((inv) => (
              <InvestmentPerfCard key={inv.id} inv={inv} period={period} compact={compact} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function InvestmentPerfCard({ inv, period, compact }) {
  const totalIncome = inv.series.reduce((a, s) => a + s.income, 0);
  const invested = inv.amount_invested;
  const roi = invested > 0 ? ((totalIncome + inv.current_value - invested) / invested) * 100 : 0;
  const bestBucket = [...inv.series].sort((a, b) => b.income - a.income)[0];

  return (
    <div className="border border-slate-200 bg-white rounded-md p-4">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0 flex-1">
          <div className="eyebrow uppercase truncate">{inv.type.replace(/_/g, " ")}</div>
          <div className="text-[14px] font-semibold text-slate-900 truncate">{inv.name}</div>
        </div>
        <div className="text-right shrink-0">
          <div className="text-[10px] tracking-wider uppercase text-slate-400">Return</div>
          <div className={`font-mono-fin text-[15px] ${roi >= 0 ? "text-green-700" : "text-red-600"}`}>
            {roi.toFixed(1)}%
          </div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2 text-[11px] mb-3">
        <div className="border border-slate-100 rounded-md p-2 bg-slate-50/50">
          <div className="text-[9px] tracking-wider uppercase text-slate-400">Invested</div>
          <div className="font-mono-fin text-slate-900 mt-0.5">{formatRupeesCompact(invested)}</div>
        </div>
        <div className="border border-slate-100 rounded-md p-2 bg-slate-50/50">
          <div className="text-[9px] tracking-wider uppercase text-slate-400">Value</div>
          <div className="font-mono-fin text-slate-900 mt-0.5">{formatRupeesCompact(inv.current_value)}</div>
        </div>
        <div className="border border-slate-100 rounded-md p-2 bg-slate-50/50">
          <div className="text-[9px] tracking-wider uppercase text-slate-400">Income</div>
          <div className="font-mono-fin text-green-700 mt-0.5">{formatRupeesCompact(totalIncome)}</div>
        </div>
      </div>

      <div className="h-[180px]">
        <ResponsiveContainer width="100%" height="100%">
          {period === "monthly" ? (
            <LineChart data={inv.series}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="bucket" tick={{ fontSize: 9 }} interval="preserveStartEnd" />
              <YAxis tick={{ fontSize: 9 }} tickFormatter={(v) => formatRupeesCompact(v)} width={60} />
              <Tooltip formatter={(v) => formatRupees(v)} contentStyle={{ fontSize: 11 }} />
              <Line type="monotone" dataKey="income" stroke="#16a34a" strokeWidth={2} name="Income" dot={{ r: 2 }} />
              <Line type="monotone" dataKey="cumulative_income" stroke="#dc2626" strokeWidth={2} name="Cumulative" strokeDasharray="4 3" dot={false} />
            </LineChart>
          ) : (
            <BarChart data={inv.series}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="bucket" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => formatRupeesCompact(v)} width={60} />
              <Tooltip formatter={(v) => formatRupees(v)} contentStyle={{ fontSize: 11 }} />
              <Bar dataKey="income" fill="#16a34a" name="Income" radius={[3, 3, 0, 0]} />
            </BarChart>
          )}
        </ResponsiveContainer>
      </div>

      {bestBucket && bestBucket.income > 0 && !compact && (
        <div className="mt-2 text-[10px] text-slate-500 flex items-center gap-1">
          <span className="font-medium text-slate-700">Best {period === "yoy" ? "year" : period === "quarterly" ? "quarter" : "month"}:</span>
          <span className="font-mono-fin">{bestBucket.bucket}</span>
          <span className="text-slate-300">·</span>
          <span className="font-mono-fin text-green-700">{formatRupees(bestBucket.income)}</span>
        </div>
      )}
    </div>
  );
}
